/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { of, Subject, timer, zip } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { Constants } from 'app/shared/models/constants.model';
import { FormHelpersService, InviteService, SignupService } from 'app/shared/services';
import * as fromSharedStore from 'app/shared/store';
import { UserData } from 'app/shared/models/user.model';

@Component({
  selector: '[setupAccountModal]',
  templateUrl: './setup-account-modal.component.html',
  styleUrls: ['./setup-account-modal.component.scss'],
})
export class SetupAccountModalComponent implements OnInit {
  @Output() closeModal: EventEmitter<any> = new EventEmitter();
  userTypes: string[];
  currentUserType: string;
  setupAccountForm: FormGroup;
  showPassword = false;
  selectedPlan: any;
  hasIndividualAthletesPurchases = false;
  hasTeamPackage = false;
  userData: UserData;

  invites = [];

  USER_TYPES = Constants.USER_TYPE;

  private emailValidator = Validators.pattern('^[a-zA-Z0-9._+-]{1,}@{1}[a-zA-Z0-9.-]{1,}[.]{1}[a-zA-Z+]{2,}');
  private componentDestroyed: Subject<any> = new Subject();

  constructor(
    public formHelpersService: FormHelpersService,
    private inviteService: InviteService,
    private sharedStore: Store<fromSharedStore.SharedState>,
    private signupService: SignupService
  ) {}

  ngOnInit() {
    this.userTypes = [Constants.USER_TYPE.ATHLETE, Constants.USER_TYPE.PARENT, Constants.USER_TYPE.COACH];
    this.setupAccountForm = new FormGroup({
      type: new FormControl(Constants.USER_TYPE.ATHLETE, [Validators.required]),
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      email: new FormControl(
        '',
        [Validators.required, Validators.email, this.sameAsAthleteEmail],
        this.validateEmail.bind(this)
      ),
      password: new FormControl('', [Validators.required, Validators.minLength(4)]),
      ageCheckmark: new FormControl(false, Validators.requiredTrue),
      rememberMe: new FormControl(false),
      username: new FormControl(''),
      plan: new FormControl(Constants.PLAN_TYPE.ONE_EVENT),
      active: new FormControl(true),
      inactiveReason: new FormControl(''),
      inviteId: new FormControl(null),
    });

    zip(
      this.sharedStore.select(fromSharedStore.getSignupState),
      this.sharedStore.select(fromSharedStore.getPurchaseState)
    )
      .pipe(take(1), takeUntil(this.componentDestroyed))
      .subscribe(([signupState, purchaseState]) => {
        this.userData = { ...signupState.userData, type: Constants.USER_TYPE.ATHLETE };
        this.selectedPlan = { ...purchaseState };
        this.getInvites();
      });

    this.setupAccountForm.get('type').valueChanges.subscribe((newUserType) => {
      this.userData.type = newUserType;
      this.getInvites();
    });
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
    this.componentDestroyed.complete();
  }

  close() {
    this.closeModal.emit({});
  }

  signIn($event) {
    $event.preventDefault();
    this.closeModal.emit({ nextAction: 'showSignIn' });
  }

  setupAccount($event) {
    $event.preventDefault();
    this.formHelpersService.markFieldsAsDirty(this.setupAccountForm);

    const allValid = this.invites.reduce((acc, invite) => {
      if (invite.form.invalid) {
        this.formHelpersService.markFieldsAsDirty(invite.form);
        return false;
      }
      return acc;
    }, true);

    // athlete invites forms not valid
    if (!this.setupAccountForm.valid || !allValid) {
      return;
    }

    this.setupAccountForm
      .get('username')
      .setValue(this.signupService.generateUsernameFromEmail(this.setupAccountForm.get('email').value));

    const accountPayload = this.setupAccountForm.value;
    accountPayload.userDetails = { ageCheckmark: accountPayload.ageCheckmark };
    this.sharedStore.dispatch(new fromSharedStore.SaveUserForSignup(accountPayload));

    // update invitees with form values
    this.invites.forEach((option) => {
      const formValue = option.form.value;

      this.sharedStore.dispatch(
        new fromSharedStore.SetInvitee({
          index: option.index,
          invitee: {
            inviteeFullName: formValue.athleteName,
            inviteeEmail: formValue.email,
          },
        })
      );
    });

    this.closeModal.emit({ nextAction: 'showCheckout' });
  }

  toggleShowPassword() {
    this.showPassword = true;
  }

  toggleHidePassword() {
    this.showPassword = false;
  }

  get type() {
    return this.setupAccountForm.get('type');
  }

  private getInvites() {
    const invitesResponse = this.inviteService.setupInvites(this.selectedPlan, this.userData);

    // if cart includes TPs - pre-select coach as user type & disable athlete option
    if (invitesResponse.includesTeamPlans) {
      // user cannot be athlete
      if (this.setupAccountForm.get('type').value === this.USER_TYPES.ATHLETE) {
        this.userTypes = this.userTypes.slice(1);
        this.setupAccountForm.get('type').setValue(this.USER_TYPES.COACH);
      }
    }

    this.invites = invitesResponse.emptyInvitees.map((invite) => {
      const purchase = this.selectedPlan.options[invite.optionIndex];

      return {
        index: invite.optionIndex,
        eventName: purchase.event ? purchase.event.name : '',
        purchaseType: Constants.PURCHASE_TYPE_NAME[purchase.type],
        form: new FormGroup({
          index: new FormControl(invite.optionIndex),
          athleteName: new FormControl(invite.inviteeFullName, Validators.required),
          email: new FormControl(invite.inviteeEmail, [Validators.required, this.emailValidator, this.sameUserEmail]),
        }),
      };
    });
  }

  // Check email exists on server and return a validation error if it does
  private validateEmail(control: FormControl) {
    // need to debounce the calls to the server to 400ms so we don't flood the server
    return timer(400).pipe(
      switchMap(() => this.signupService.checkEmailValid(control.value)),
      switchMap((result: any) => {
        if (result && result.data && !result.data.valid) {
          return of({ email: true });
        } else {
          return this.signupService.checkUserExists({ email: control.value }).pipe(
            map((existsResult: any) => {
              if (existsResult && existsResult.data && existsResult.data.length > 0) {
                return { emailExists: true };
              } else {
                return null;
              }
            })
          );
        }
      })
    );
  }

  private sameUserEmail = (control: AbstractControl) => {
    if (control.value === this.setupAccountForm.get('email').value) {
      return { sameUserEmail: true };
    }
    return null;
  };

  private sameAsAthleteEmail = (control: AbstractControl) => {
    let isSameEmail = false;
    if (this.invites && this.invites.length) {
      isSameEmail = this.invites.some((invite) => {
        return invite.form && control.value === invite.form.get('email').value;
      });
    }
    return isSameEmail ? { sameAsAthleteEmail: isSameEmail } : null;
  };
}
